import {Dropdown, Tabs} from 'antd'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useContext, useEffect, useMemo} from 'react'
import {Switch, useParams} from 'react-router-dom'
import {gConfigs, gPaths} from 'views/AppLocals/configs'
import {mapPathParam} from 'views/AppLocals/functions/routerHelper'
import useNavRouteAction from 'views/AppLocals/hooks/useNavRouteAction'
import {NavRoutes} from 'views/AppLocals/hooks/useNavRoutes'
import {notEmpty, NullAsDefault} from 'views/Shared'
import {Queries} from 'views/Shared/enums/Queries'
import {SubDomainContext} from 'views/SubDomain/SubDomainContext'
import OrganizationContainerContext from '../OrganizationContainerContext'
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext"
import {FiMoreHorizontal} from "react-icons/fi"
import {AiOutlineHome} from "react-icons/ai"
import {useOrgTypeLabel} from "../../../../modules/local/useOrgTypeLabels"

const OrganizationNav = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const {isSubDomain, url_domain, url_pathname, ...rest} =
        useContext(SubDomainContext)

    const params = useParams()

    const history = useHistory()

    const id = _.get(params, 'id')

    const withSubDomain = Boolean(isSubDomain && !id)

    const rootPath = withSubDomain
        ? gPaths.organization_domain
        : gPaths.organization

    const {
        isValidRoute = NullAsDefault,
        routeEntities = [],
        getDefaultAction = NullAsDefault,
        organization = {},
        set_route_data = NullAsDefault,
    } = useContext(OrganizationContainerContext)

    const defaultAction = "news"

    // Tab "groups" dùng thuật ngữ riêng theo org_type (Thuật ngữ & Nhãn), fallback về text tĩnh nếu chưa khai báo
    const deptLabel = useOrgTypeLabel(organization?.org_type, 'group', organization?.label_privacy) || t('groups / ministries')
    const resolveBreadcrumbName = useCallback(
        (path, breadcrumbName) => path === 'groups' ? deptLabel : breadcrumbName(t),
        [t, deptLabel]
    )

    const {currentAction} = useNavRouteAction({
        anchor: id,
        defaultAction,
        isAction: isValidRoute,
        getActionParam: withSubDomain
            ? (pathname) => {
                const pattern =
                    rootPath == '' ? pathname : _.last(String(pathname).split(rootPath))
                const _action = pattern.substring(pattern.startsWith('/') ? 1 : 0)
                return _.isEmpty(_action.trim()) ? defaultAction : _action
            }
            : undefined,
    })

    useEffect(() => {
        set_route_data({
            [Queries.REL]: currentAction,
        })
    }, [currentAction])

    const pathTransform = useCallback(
        (path, segment) =>
            [
                _.isEmpty(id)
                    ? path
                    : mapPathParam(path, {
                        id,
                    }),
                segment,
            ]
                .filter(notEmpty)
                .join('/'),
        [id]
    )

    const visibleRoutes = useMemo(
        () => routeEntities.filter(
            ({isInvisible = NullAsDefault}) => !isInvisible({item: organization})
        ),
        [organization, routeEntities]
    )

    const tabItems = useMemo(
        () =>
            visibleRoutes.map(({icon, path, breadcrumbName = NullAsDefault}, index) => ({
                key: path,
                label: index === 0 ? (
                    <span className="flex items-center px-1">
                        <AiOutlineHome size={16}/>
                    </span>
                ) : icon ? (
                    <span className="flex items-center gap-2">
                        {icon} {resolveBreadcrumbName(path, breadcrumbName)}
                    </span>
                ) : resolveBreadcrumbName(path, breadcrumbName),
                children: (
                    <NavRoutes
                        rootPath={rootPath}
                        routes={routeEntities}
                        transform={pathTransform}
                        defaultAction={defaultAction}
                        renderRoutes={
                            withSubDomain
                                ? (routes, redirect) => (
                                    <Switch>
                                        {routes}
                                        {redirect}
                                    </Switch>
                                )
                                : undefined
                        }
                    />
                ),
            })),
        [
            t,
            rootPath,
            defaultAction,
            visibleRoutes,
            routeEntities,
            pathTransform,
            withSubDomain,
            resolveBreadcrumbName,
        ]
    )

    const dropdownItems = useMemo(
        () => visibleRoutes.map(({icon, path, breadcrumbName = NullAsDefault}, index) => ({
            key: path,
            icon: index === 0 ? <AiOutlineHome size={14}/> : icon,
            label: resolveBreadcrumbName(path, breadcrumbName),
        })),
        [t, visibleRoutes, resolveBreadcrumbName]
    )

    return (
        <Wrapper id="contentOrg" className="background-50">
            <Tabs
                id="tab_content" type="line"
                className="custom-tabs-organization"
                tabBarStyle={gConfigs.styles2.tabBar}
                destroyOnHidden={false}
                onChange={(activeKey) => {
                    history.push({
                        pathname: pathTransform(rootPath, activeKey),
                    })
                }}
                items={tabItems}
                activeKey={currentAction}
                tabBarExtraContent={!isLg && (
                    <Dropdown
                        menu={{
                            items: dropdownItems,
                            selectedKeys: [currentAction],
                            onClick: ({key}) => {
                                history.push({
                                    pathname: pathTransform(rootPath, key),
                                })
                            },
                        }}
                        trigger={['click']}
                        placement="bottomRight">
                        <button className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-color-50 mx-1 text-color-300">
                            <FiMoreHorizontal size={16}/>
                        </button>
                    </Dropdown>
                )}
            />
        </Wrapper>
    )
}

export default OrganizationNav
